import { createAppointment } from "./firebase/booking-api";
import { createModal } from "./ui/modal";

(function () {
  "use strict";

  const STYLE_ID = "lior-booking-widget-styles";

  const styles = `
  /* Design System - CSS Variables */
  :root {
    --lb-bg: #ffffff;
    --lb-card: #ffffff;
    --lb-primary: #f97316;
    --lb-primary-end: #ef4444;
    --lb-primary-soft: #fed7aa;
    --lb-secondary: #6b7280;
    --lb-border: #e5e7eb;
    --lb-text: #111827;
    --lb-text-muted: #6b7280;
    --lb-success: #10b981;
    --lb-success-bg: #d1fae5;
    --lb-error: #ef4444;
    --lb-error-bg: #fee2e2;
    --lb-space-xs: 4px;
    --lb-space-sm: 8px;
    --lb-space-md: 16px;
    --lb-space-lg: 24px;
    --lb-space-xl: 32px;
    --lb-radius-sm: 8px;
    --lb-radius-md: 12px;
    --lb-radius-lg: 18px;
    --lb-radius-full: 999px;
    --lb-shadow-sm: 0 2px 8px rgba(0, 0, 0, 0.1);
    --lb-shadow-md: 0 10px 25px rgba(0, 0, 0, 0.15);
    --lb-shadow-lg: 0 20px 60px rgba(0, 0, 0, 0.3);
    --lb-shadow-elevated: 0 22px 45px rgba(15, 23, 42, 0.6);
    --lb-transition-fast: 0.15s ease;
    --lb-transition-base: 0.2s ease;
    --lb-transition-slow: 0.3s ease;
  }
  
  /* Dark Mode Support */
  @media (prefers-color-scheme: dark) {
    :root {
      --lb-bg: #020617;
      --lb-card: #0f172a;
      --lb-primary: #f97316;
      --lb-primary-end: #ef4444;
      --lb-primary-soft: #7c2d12;
      --lb-secondary: #9ca3af;
      --lb-border: #1f2937;
      --lb-text: #f9fafb;
      --lb-text-muted: #9ca3af;
      --lb-success: #10b981;
      --lb-success-bg: #064e3b;
      --lb-error: #ef4444;
      --lb-error-bg: #7f1d1d;
    }
  }
  
  .lior-booking-button {
    position: fixed;
    bottom: 28px;
    inset-inline-end: 28px;
    border: none;
    border-radius: var(--lb-radius-full);
    padding: 14px 22px;
    --lbg-start: var(--lb-primary);
    --lbg-end: var(--lb-primary-end);
    background: linear-gradient(135deg, var(--lbg-start), var(--lbg-end));
    color: #fff;
    font-size: 1rem;
    font-weight: 700;
    font-family: "Heebo", "Alef", "Assistant", sans-serif;
    cursor: pointer;
    box-shadow: var(--lb-shadow-md);
    z-index: 2147483000;
    transition: transform var(--lb-transition-base), box-shadow var(--lb-transition-base);
    text-align: start;
    display: inline-flex;
    align-items: center;
    gap: var(--lb-space-sm);
  }
  .lior-booking-icon {
    font-size: 1.2rem;
    line-height: 1;
    flex-shrink: 0;
  }
  .lior-booking-label {
    display: flex;
    flex-direction: column;
    gap: 2px;
  }
  .lior-booking-label-main {
    display: block;
    font-size: 1rem;
    font-weight: 700;
    line-height: 1.2;
  }
  .lior-booking-label-sub {
    display: block;
    font-size: 0.75rem;
    font-weight: 400;
    opacity: 0.9;
    line-height: 1.2;
  }
  .lior-booking-button:hover {
    transform: translateY(-2px);
    box-shadow: var(--lb-shadow-lg);
    background: linear-gradient(135deg, var(--lb-primary), var(--lb-primary-end));
    background-size: 120% 120%;
    background-position: center;
    animation: lior-booking-gradient-shift 2s ease infinite;
  }
  @keyframes lior-booking-gradient-shift {
    0%, 100% { background-position: 0% 50%; }
    50% { background-position: 100% 50%; }
  }
  
  /* Hide floating button when modal is open */
  .lior-booking-button.lior-booking-hidden {
    opacity: 0;
    pointer-events: none;
    transform: translateY(12px);
    transition: opacity var(--lb-transition-base), transform var(--lb-transition-base);
  }
  .lior-booking-button:active {
    transform: translateY(0);
  }
  .lior-booking-button:focus-visible {
    outline: 3px solid #fde68a;
    outline-offset: 3px;
  }
  
  /* Modal Overlay */
  .lior-booking-modal-overlay {
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.5);
    backdrop-filter: blur(4px);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 2147482999;
    opacity: 0;
    visibility: hidden;
    transition: opacity var(--lb-transition-base), visibility var(--lb-transition-base);
    padding: var(--lb-space-md);
  }
  .lior-booking-modal-overlay.is-open {
    opacity: 1;
    visibility: visible;
  }
  
  /* Modal Box */
  .lior-booking-modal {
    background: var(--lb-card);
    border-radius: var(--lb-radius-lg);
    width: 100%;
    max-width: 420px;
    max-height: 90vh;
    overflow-y: auto;
    box-shadow: var(--lb-shadow-elevated);
    transform: scale(0.96) translateY(10px);
    opacity: 0;
    transition: transform var(--lb-transition-slow) ease-out, opacity var(--lb-transition-slow) ease-out;
    font-family: "Heebo", "Alef", "Assistant", sans-serif;
    color: var(--lb-text);
  }
  .lior-booking-modal-overlay.is-open .lior-booking-modal {
    transform: scale(1) translateY(0);
    opacity: 1;
  }
  
  /* Modal Header */
  .lior-booking-modal header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: var(--lb-space-lg) var(--lb-space-lg) var(--lb-space-md);
    border-bottom: 1px solid var(--lb-border);
    position: relative;
  }
  .lior-booking-modal header::before {
    content: "קביעת תור מהירה";
    position: absolute;
    top: var(--lb-space-sm);
    right: var(--lb-space-lg);
    font-size: 0.7rem;
    text-transform: uppercase;
    letter-spacing: 0.08em;
    color: var(--lb-text-muted);
    font-weight: 600;
  }
  .lior-booking-modal h2 {
    margin: 14px 0 0;
    font-size: 1.5rem;
    font-weight: 700;
    color: var(--lb-text);
  }
  .lior-booking-modal-description {
    margin: var(--lb-space-xs) 0 0;
    font-size: 0.875rem;
    color: var(--lb-text-muted);
    font-weight: 400;
  }
  .lior-booking-close {
    background: none;
    border: none;
    font-size: 28px;
    line-height: 1;
    color: var(--lb-text-muted);
    cursor: pointer;
    padding: var(--lb-space-xs) var(--lb-space-sm);
    border-radius: var(--lb-radius-sm);
    transition: background var(--lb-transition-base), color var(--lb-transition-base);
    width: 32px;
    height: 32px;
    display: flex;
    align-items: center;
    justify-content: center;
  }
  .lior-booking-close:hover {
    background: var(--lb-border);
    color: var(--lb-text);
  }
  .lior-booking-close:focus-visible {
    outline: 2px solid var(--lb-primary);
    outline-offset: 2px;
  }
  
  /* Form */
  .lior-booking-form {
    padding: var(--lb-space-lg);
    display: flex;
    flex-direction: column;
    gap: var(--lb-space-md);
    border-top: 1px solid var(--lb-border);
  }
  .lior-booking-form-row-2 {
    display: flex;
    flex-direction: column;
    gap: var(--lb-space-md);
  }
  .lior-booking-form-field {
    display: flex;
    flex-direction: column;
    gap: var(--lb-space-sm);
    position: relative;
  }
  .lior-booking-form-field span {
    font-size: 0.8rem;
    font-weight: 600;
    color: var(--lb-text);
    letter-spacing: 0.01em;
    text-transform: none;
  }
  .lior-booking-form-field input,
  .lior-booking-form-field textarea {
    width: 100%;
    padding: 12px var(--lb-space-md);
    border: 2px solid var(--lb-border);
    border-radius: var(--lb-radius-md);
    font-size: 1rem;
    font-family: inherit;
    transition: border-color var(--lb-transition-base), box-shadow var(--lb-transition-base);
    background: var(--lb-card);
    color: var(--lb-text);
  }
  .lior-booking-form-field input:focus,
  .lior-booking-form-field textarea:focus {
    outline: none;
    border-color: var(--lb-primary);
    box-shadow: 0 0 0 3px var(--lb-primary-soft);
    transform: translateY(-1px);
    transition: all var(--lb-transition-base);
  }
  .lior-booking-form-field textarea {
    resize: vertical;
    min-height: 80px;
  }
  .lior-booking-submit {
    margin-top: var(--lb-space-sm);
    padding: 14px var(--lb-space-lg);
    background: linear-gradient(135deg, var(--lb-primary), var(--lb-primary-end));
    color: #fff;
    border: none;
    border-radius: var(--lb-radius-md);
    font-size: 1rem;
    font-weight: 700;
    font-family: inherit;
    cursor: pointer;
    transition: transform var(--lb-transition-base), box-shadow var(--lb-transition-base), opacity var(--lb-transition-base);
    box-shadow: var(--lb-shadow-sm);
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: var(--lb-space-sm);
  }
  .lior-booking-submit:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: var(--lb-shadow-md);
  }
  
  /* Spinner */
  .lior-booking-spinner {
    width: 16px;
    height: 16px;
    border: 2px solid rgba(255, 255, 255, 0.3);
    border-top-color: #fff;
    border-radius: 50%;
    animation: lior-booking-spin 0.6s linear infinite;
  }
  @keyframes lior-booking-spin {
    to { transform: rotate(360deg); }
  }
  .lior-booking-submit:active:not(:disabled) {
    transform: translateY(0);
  }
  .lior-booking-submit:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
  .lior-booking-submit:focus-visible {
    outline: 3px solid #fde68a;
    outline-offset: 3px;
  }
  
  /* Message */
  .lior-booking-message {
    margin-top: 16px;
    padding: 12px 16px;
    border-radius: 10px;
    font-size: 0.875rem;
    font-weight: 500;
    text-align: center;
    display: none;
    transform: translateY(6px);
    opacity: 0;
  }
  .lior-booking-message.success {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: var(--lb-space-sm);
    background: var(--lb-success-bg);
    color: var(--lb-success);
    border: 1px solid var(--lb-success);
    animation: bookingMessageIn 0.18s ease-out forwards;
  }
  .lior-booking-message.error {
    display: flex;
    align-items: flex-start;
    justify-content: flex-start;
    gap: var(--lb-space-sm);
    background: var(--lb-error-bg);
    color: var(--lb-error);
    border: 1px solid var(--lb-error);
    animation: bookingMessageIn 0.18s ease-out forwards;
    text-align: start;
    padding: var(--lb-space-md);
  }
  .lior-booking-message.error > span:first-child {
    flex-shrink: 0;
    margin-top: 2px;
  }
  
  /* Toast Notification */
  .lior-booking-toast {
    position: fixed;
    bottom: 80px;
    inset-inline-end: 28px;
    background: var(--lb-card);
    color: var(--lb-text);
    padding: var(--lb-space-md) var(--lb-space-lg);
    border-radius: var(--lb-radius-md);
    box-shadow: var(--lb-shadow-elevated);
    z-index: 2147483001;
    display: flex;
    align-items: center;
    gap: var(--lb-space-sm);
    max-width: 320px;
    transform: translateY(20px);
    opacity: 0;
    transition: transform var(--lb-transition-base), opacity var(--lb-transition-base);
    border: 1px solid var(--lb-border);
  }
  .lior-booking-toast.show {
    transform: translateY(0);
    opacity: 1;
  }
  .lior-booking-toast.success {
    border-color: var(--lb-success);
  }
  
  /* Success Screen */
  .lior-booking-success-screen {
    padding: var(--lb-space-xl);
    text-align: center;
  }
  .lior-booking-success-icon {
    font-size: 3rem;
    margin-bottom: var(--lb-space-md);
  }
  .lior-booking-success-title {
    font-size: 1.5rem;
    font-weight: 700;
    color: var(--lb-text);
    margin-bottom: var(--lb-space-md);
  }
  .lior-booking-success-card {
    background: var(--lb-primary-soft);
    border: 1px solid var(--lb-primary);
    border-radius: var(--lb-radius-md);
    padding: var(--lb-space-lg);
    margin: var(--lb-space-lg) 0;
    text-align: start;
  }
  .lior-booking-success-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: var(--lb-space-sm) 0;
    border-bottom: 1px solid rgba(249, 115, 22, 0.2);
  }
  .lior-booking-success-row:last-child {
    border-bottom: none;
  }
  .lior-booking-success-label {
    font-size: 0.875rem;
    color: var(--lb-text-muted);
    font-weight: 600;
  }
  .lior-booking-success-value {
    font-size: 1rem;
    color: var(--lb-text);
    font-weight: 700;
  }
  @keyframes bookingMessageIn {
    from {
      opacity: 0;
      transform: translateY(6px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
  
  /* Wizard */
  .lior-booking-wizard {
    width: 100%;
  }
  .lior-booking-wizard-step {
    display: flex;
    flex-direction: column;
    gap: var(--lb-space-md);
    min-height: 200px;
  }
  .lior-booking-wizard-title {
    font-size: 1.1rem;
    font-weight: 700;
    color: var(--lb-text);
    margin: 0 0 4px 0;
  }
  .lior-booking-wizard-buttons {
    display: flex;
    gap: var(--lb-space-sm);
    margin-top: var(--lb-space-sm);
  }
  .lior-booking-secondary {
    padding: 14px var(--lb-space-lg);
    background: transparent;
    color: var(--lb-text-muted);
    border: 2px solid var(--lb-border);
    border-radius: var(--lb-radius-md);
    font-size: 1rem;
    font-weight: 600;
    font-family: inherit;
    cursor: pointer;
    transition: all var(--lb-transition-base);
    flex: 1;
  }
  .lior-booking-secondary:hover {
    background: var(--lb-border);
    color: var(--lb-text);
  }
  .lior-booking-wizard-buttons .lior-booking-submit {
    flex: 2;
    margin-top: 0;
  }
  
  /* Desktop - Date/Time in row */
  @media (min-width: 600px) {
    .lior-booking-form-row-2 {
      flex-direction: row;
      gap: 12px;
    }
    .lior-booking-form-row-2 .lior-booking-form-field {
      flex: 1;
    }
    .lior-booking-wizard-buttons {
      flex-direction: row-reverse;
    }
    .lior-booking-wizard-buttons .lior-booking-submit {
      flex: 1;
    }
  }
  
  /* Responsive - Mobile Bottom Sheet */
  @media (max-width: 600px) {
    /* כפתור קטן יותר בטלפון */
    .lior-booking-button {
      bottom: 20px;
      inset-inline-end: 20px;
      padding: 10px 16px;
      font-size: 0.875rem;
      box-shadow: 0 6px 16px rgba(255, 85, 54, 0.35);
    }
    .lior-booking-label-sub {
      font-size: 0.7rem;
    }
    
    /* Bottom Sheet - מודאל צמוד לתחתית */
    .lior-booking-modal-overlay {
      padding: 0;
      align-items: flex-end;
    }
    
    .lior-booking-modal {
      position: fixed;
      inset: auto 0 0 0;
      margin: 0;
      max-width: 100%;
      max-height: calc(100vh - 72px);
      border-radius: 24px 24px 0 0;
      box-shadow: 0 -10px 30px rgba(0, 0, 0, 0.25);
      display: flex;
      flex-direction: column;
      height: auto;
    }
    
    /* פס גרירה */
    .lior-booking-modal::before {
      content: "";
      display: block;
      width: 46px;
      height: 4px;
      border-radius: 999px;
      background: #4b5563;
      margin: 8px auto 2px;
      flex-shrink: 0;
    }
    
    /* Header קומפקטי */
    .lior-booking-modal header {
      padding: 10px 20px 4px;
      flex-shrink: 0;
      border-bottom: 1px solid var(--lb-border);
    }
    .lior-booking-modal header::before {
      display: none; /* הסר את ה-label במובייל */
    }
    .lior-booking-modal h2 {
      font-size: 1.1rem;
      margin: 0;
    }
    .lior-booking-modal-description {
      font-size: 0.82rem;
      margin: 4px 0 0;
      color: #9ca3af;
    }
    .lior-booking-close {
      font-size: 24px;
      width: 28px;
      height: 28px;
    }
    
    /* Body - גלילה */
    .lior-booking-form {
      padding: 8px 20px 16px;
      gap: 12px;
      overflow-y: auto;
      flex: 1;
      -webkit-overflow-scrolling: touch;
    }
    
    /* שדות קומפקטיים */
    .lior-booking-form-field {
      gap: 4px;
    }
    .lior-booking-form-field span {
      font-size: 0.8rem;
      color: #9ca3af;
      margin-bottom: 4px;
    }
    .lior-booking-form-field input,
    .lior-booking-form-field textarea {
      padding: 10px 12px;
      font-size: 0.95rem;
      border-radius: 8px;
    }
    .lior-booking-form-field textarea {
      min-height: 70px;
    }
    
    /* Wizard title קומפקטי */
    .lior-booking-wizard-title {
      font-size: 0.95rem;
      margin: 6px 0 4px;
    }
    
    /* כפתורים תחתונים - ברוחב מלא */
    .lior-booking-wizard-buttons {
      padding: 10px 16px calc(12px + env(safe-area-inset-bottom));
      border-top: 1px solid rgba(148, 163, 184, 0.2);
      margin: 0 -20px -16px;
      background: var(--lb-card);
      flex-shrink: 0;
      position: sticky;
      bottom: 0;
      display: flex;
      gap: var(--lb-space-sm);
    }
    .lior-booking-wizard-step .lior-booking-submit {
      width: 100%;
      border-radius: 999px;
      padding: 13px 0;
      font-size: 0.95rem;
      margin-top: 0;
    }
    .lior-booking-secondary {
      width: 100%;
      border-radius: 999px;
      padding: 13px 0;
    }
    
    /* הודעות קטנות יותר */
    .lior-booking-message {
      padding: 10px 12px;
      font-size: 0.8125rem;
      margin-top: 8px;
    }
  }
  
  /* Responsive - Small Mobile */
  @media (max-width: 480px) {
    .lior-booking-button {
      bottom: 16px;
      inset-inline-end: 16px;
    }
  }
  
  /* Responsive - Small Mobile */
  @media (max-width: 360px) {
    .lior-booking-button {
      bottom: 12px;
      inset-inline-end: 12px;
      padding: 8px 14px;
      font-size: 0.8125rem;
    }
    .lior-booking-modal {
      max-height: calc(100vh - 60px);
    }
    .lior-booking-modal header {
      padding: 8px 16px 4px;
    }
    .lior-booking-form {
      padding: 6px 16px 12px;
    }
    .lior-booking-success-screen {
      padding: var(--lb-space-lg);
    }
  }
  
  /* Extra small screens - iPhone SE, etc */
  @media (max-width: 320px) {
    .lior-booking-modal {
      max-height: calc(100vh - 50px);
      border-radius: 20px 20px 0 0;
    }
    .lior-booking-form {
      padding: 6px 14px 12px;
    }
  }
  `;

  function injectStyles() {
    if (document.getElementById(STYLE_ID)) return;
    const styleEl = document.createElement("style");
    styleEl.id = STYLE_ID;
    styleEl.textContent = styles;
    document.head.appendChild(styleEl);
  }

  function resolveScriptElement() {
    if (document.currentScript) return document.currentScript;
    const scripts = document.querySelectorAll('script[data-client-id]');
    return scripts[scripts.length - 1] || null;
  }

  function init() {
    const scriptEl = resolveScriptElement();
    const clientId = scriptEl?.getAttribute("data-client-id");

    if (!clientId) {
      console.warn("Booking Widget: missing data-client-id attribute");
      return;
    }

    injectStyles();

    const buttonLabel = scriptEl.getAttribute("data-button-label") || "קבע תור";
    const modalTitle = scriptEl.getAttribute("data-modal-title") || "קביעת תור";

    const button = document.createElement("button");
    button.type = "button";
    button.className = "lior-booking-button";
    button.innerHTML = `
      <span class="lior-booking-icon" aria-hidden="true">📅</span>
      <span class="lior-booking-label">
        <span class="lior-booking-label-main">${buttonLabel}</span>
        <span class="lior-booking-label-sub">פחות מדקה וסיימת</span>
      </span>
    `;

    const modal = createModal({ title: modalTitle });

    document.body.appendChild(button);
    document.body.appendChild(modal.container);

    button.addEventListener("click", () => {
      modal.open();
    });

    modal.onSubmit(async (formData) => {
      try {
        await createAppointment(clientId, formData);
        modal.showSuccess("התור נקבע בהצלחה, נחזור אליך בהקדם");
      } catch (error) {
        console.error("Booking Widget error", error);
        const isNetworkError = !navigator.onLine || 
          error.message?.includes('network') || 
          error.message?.includes('fetch') ||
          error.code === 'unavailable';
        modal.showError("אירעה שגיאה, נסה שוב מאוחר יותר", isNetworkError);
      }
    });
    
    // Check for theme color
    const themeColor = scriptEl.getAttribute("data-theme-color");
    if (themeColor) {
      button.style.setProperty("--lb-primary", themeColor);
      button.style.setProperty("--lb-primary-end", themeColor);
    }
    
    // Check for dark mode preference
    const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
    if (prefersDark) {
      document.documentElement.classList.add('lior-booking-dark');
    }
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init, { once: true });
  } else {
    init();
  }
})();
